import { Fragment } from "react";
import { createPortal } from "react-dom";
import { Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import type { ToastItem } from "@/context/ToastContext";

// ── Per-category visual tokens ────────────────────────────────

const CONFIG = {
  success: {
    Icon: CheckCircleIcon,
    wrapper:
      "bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800",
    text: "text-green-800 dark:text-green-200",
    icon: "text-green-500",
  },
  warning: {
    Icon: ExclamationTriangleIcon,
    wrapper:
      "bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800",
    text: "text-amber-800 dark:text-amber-200",
    icon: "text-amber-500",
  },
  error: {
    Icon: XCircleIcon,
    wrapper: "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800",
    text: "text-red-800 dark:text-red-200",
    icon: "text-red-500",
  },
} as const;

// ── Single toast item ─────────────────────────────────────────

function ToastItemEl({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: () => void;
}) {
  const { Icon, wrapper, text, icon } = CONFIG[toast.category];

  return (
    <Transition
      as={Fragment}
      appear
      show
      enter="transition ease-out duration-200"
      enterFrom="opacity-0 translate-y-2"
      enterTo="opacity-100 translate-y-0"
      leave="transition ease-in duration-150"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 translate-y-2"
    >
      <div
        role="alert"
        data-testid={`toast-${toast.category}`}
        onClick={onDismiss}
        className={[
          "flex items-start gap-3 px-4 py-3 rounded-card border shadow-md cursor-pointer",
          "max-w-sm w-full text-sm",
          wrapper,
          text,
        ].join(" ")}
      >
        <Icon
          className={`h-5 w-5 flex-shrink-0 mt-0.5 ${icon}`}
          aria-hidden="true"
        />
        <span>{toast.message}</span>
      </div>
    </Transition>
  );
}

// ── Container (portal) ────────────────────────────────────────

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="false"
      data-testid="toast-container"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0 flex flex-col gap-2 z-[60]"
    >
      {toasts.map((toast) => (
        <ToastItemEl
          key={toast.id}
          toast={toast}
          onDismiss={() => onDismiss(toast.id)}
        />
      ))}
    </div>,
    document.body
  );
}
